import { Instagram, MessageCircle, Youtube } from 'lucide-react'
import PublicFooter, { type FooterColumn } from './PublicFooter'
import {
  BEWAVE_BLUE,
  BEWAVE_WHATSAPP_URL,
  BEWAVE_YOUTUBE_URL,
  EXPLORE_PATH,
} from './publicShell'
import { CREATE_PAGE_LABEL, createPageHref } from './CreateYourPage'
import { trackWhatsAppClick } from '@/lib/analytics'
import type { CityOption } from '@/types/publicSchool'

export { BEWAVE_WHATSAPP_URL, BEWAVE_YOUTUBE_URL }
export const BEWAVE_INSTAGRAM_URL = 'https://www.instagram.com/bewave.app/'

/** Quantas cidades cabem na coluna do rodapé antes do "Ver todas". */
const FOOTER_CITIES = 6

interface BewaveFooterProps {
  /**
   * Vira a coluna "Cidades". Só o Explore tem essa lista em mãos — as outras
   * páginas públicas não carregam as escolas todas só para montar o rodapé.
   */
  cities?: CityOption[]
  /**
   * Colunas da página, à esquerda das da plataforma. É por aqui que a página da
   * escola entra com as âncoras dela e o contato, sem duplicar o resto.
   */
  extraColumns?: FooterColumn[]
  /** Reserva espaço para barra fixa no mobile (a de reserva, na página da escola). */
  clearsFixedBar?: boolean
  className?: string
  /**
   * Tira o convite "Criar sua página" — só a landing comercial (Home) usa
   * isto. Fica só o link "BeWave Explore" na coluna Descubra: é o único
   * vínculo que a home mantém com o diretório, de propósito (ajuda a
   * indexação do Explore sem competir por espaço com a landing).
   */
  hideExploreLinks?: boolean
  /**
   * Origem absoluta do Explore (`https://explore.bewave.surf`), para o rodapé
   * da landing comercial conseguir apontar para OUTRO host. Vem do servidor
   * (HomeController), não hardcoded, porque dev/produção têm hosts diferentes.
   *
   * Dentro do próprio Explore não é passada: lá o link é relativo mesmo.
   */
  exploreOrigin?: string
}

/**
 * Rodapé da plataforma: o conteúdo BeWave (marca, links, redes, legais) que é
 * igual na landing, no diretório e na página da escola.
 *
 * O esqueleto visual mora em PublicFooter; o que está aqui é só o conteúdo, que
 * antes vivia copiado dentro de cada página — e por isso divergia.
 */
export default function BewaveFooter({
  cities = [],
  extraColumns = [],
  clearsFixedBar = false,
  className,
  hideExploreLinks = false,
  exploreOrigin,
}: BewaveFooterProps) {
  const year = new Date().getFullYear()

  // Sem origem, link relativo — é o caso de dentro do próprio Explore.
  const exploreHref = exploreOrigin ?? EXPLORE_PATH
  const cityHref = (slug: string) =>
    exploreOrigin
      ? `${exploreOrigin}/?city=${slug}`
      : `${EXPLORE_PATH}?city=${slug}`

  const columns: FooterColumn[] = [...extraColumns]

  // hideExploreLinks: além de nunca ter recebido `cities` hoje, a landing não
  // deve ganhar esta coluna se algum dia passar a receber — ela é, de ponta a
  // ponta, um jeito de navegar pelo diretório.
  if (cities.length > 0 && !hideExploreLinks) {
    columns.push({
      title: 'Cidades',
      // Teto de 6: a coluna mais alta é quem define a altura do rodapé, e sem
      // limite ela cresce uma linha por cidade nova — com trinta cidades o
      // rodapé viraria uma página. O "Ver todas" leva ao diretório, que é onde
      // a lista completa faz sentido.
      links: [
        ...cities.slice(0, FOOTER_CITIES).map((city) => ({
          label: city.label,
          href: cityHref(city.slug),
        })),
        ...(cities.length > FOOTER_CITIES
          ? [{ label: 'Ver todas', href: exploreHref }]
          : []),
      ],
    })
  }

  columns.push(
    {
      title: 'Descubra',
      links: [{ label: 'BeWave Explore', href: exploreHref }],
    },
    {
      title: 'Para negócios',
      links: [
        { label: 'Conheça a BeWave', href: '/' },
        // O mesmo convite do CTA das páginas, aqui como link: quem rolou até o
        // rodapé procurando "como entro nisso" não devia ter que subir de volta.
        // Não é `external`: `createPageHref()` já é rota interna (`CREATE_PAGE_PATH`).
        // Ausente na landing comercial: ela já não pitcha o Explore como produto
        // à parte, só deixa o link "BeWave Explore" acima como porta de entrada.
        ...(hideExploreLinks
          ? []
          : [{ label: CREATE_PAGE_LABEL, href: createPageHref() }]),
        {
          label: 'Falar com a gente',
          href: BEWAVE_WHATSAPP_URL,
          external: true,
          onClick: () =>
            trackWhatsAppClick({ source: 'footer', listing_type: 'bewave' }),
        },
        { label: 'Entrar', href: '/sign_in' },
      ],
    },
    {
      title: 'Ajuda',
      links: [
        { label: 'Suporte', href: '/support' },
        { label: 'Excluir conta', href: '/delete-account' },
      ],
    },
  )

  return (
    <PublicFooter
      brand={{
        name: 'BeWave',
        // Sem assinatura: "Menos planilha. Mais tempo na água." é a promessa do
        // BeWave Admin (gestão da escola), e este rodapé serve também o Explore,
        // que vende o oposto — ser encontrado por cliente novo.
        color: BEWAVE_BLUE,
        // Sem logo: o header é sticky e carrega a marca durante a página toda.
        // Repetir aqui embaixo custava 36px de altura e não dizia nada novo.
        showMark: false,
      }}
      columns={columns}
      legalLinks={[
        { label: 'Política de privacidade', href: '/policy-privacy' },
        { label: 'Suporte', href: '/support' },
      ]}
      social={[
        { label: 'Instagram', href: BEWAVE_INSTAGRAM_URL, icon: Instagram },
        { label: 'YouTube', href: BEWAVE_YOUTUBE_URL, icon: Youtube },
        { label: 'WhatsApp', href: BEWAVE_WHATSAPP_URL, icon: MessageCircle },
      ]}
      copyright={`© ${year} BeWave. Todos os direitos reservados.`}
      clearsFixedBar={clearsFixedBar}
      className={className}
    />
  )
}
